import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components/macro";
import { NavLink, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  CardContent,
  Grid,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  Divider as MuiDivider,
  Typography as MuiTypography,
  Alert as MuiAlert,
  Accordion,
  AccordionSummary as MuiAccordionSummary,
  AccordionDetails as MuiAccordionDetails,
  Button,
  IconButton,
  Paper,
  Skeleton,
} from "@mui/material";
import { Formik, Field } from "formik";
import * as Yup from "yup";
import { spacing, sizing, display, flexbox } from "@mui/system";
import axios from "../../utils/axios";
import {
  COLORS_USER_STATUSES,
  COUNTRIES,
  GAME_USER_DEPOSIT_VIEW,
  GAME_USER_HISTORY_MONEY_VIEW,
  GAME_USER_MODIFY,
  GAME_USER_REFUNDS_VIEW,
  GAME_USER_WITHDRAWAL_VIEW,
  USER_TYPES,
} from "../../constants";
import HomeIcon from "@mui/icons-material/Home";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import convertUSD from "../../utils/convertUSD";
import UserMatches from "./components/UserMatches";
import UserDeposits from "./components/UserDeposits";
import UserRefunds from "./components/UserRefunds";
import UserMoneyHistory from "./components/UserMoneyHistory";
import convertUTC from "../../utils/convertUTC";
import useAuth from "../../hooks/useAuth";
import { checkPermissions } from "../../utils/checkPermissions";
import {
  SingleSelect,
  NumberField,
  MultilineTextField,
} from "../../utils/form";
import { CircularIndeterminate } from "../components/Progress";
import UserWithdraws from "./components/UserWithdraws";
import StatusChip from "../../components/StatusChip";
import useConfirm from "../../components/useConfirm";

const Card = styled(MuiCard)(spacing, sizing, display, flexbox);

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Typography = styled(MuiTypography)(display);

const Alert = styled(MuiAlert)(spacing);

const AccordionSummary = styled(MuiAccordionSummary)(({ theme }) => ({
  padding: theme.spacing(0, 4),
  "&.Mui-expanded": {
    minHeight: "unset",
  },
  "& .Mui-expanded": {
    margin: theme.spacing(3, 0),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(spacing);

const UserTables = () => {
  const { permissions } = useAuth();

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper elevation={0}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} px={4}>
              <Typography>User Matches</Typography>
            </AccordionSummary>
            <AccordionDetails px={0}>
              <UserMatches />
            </AccordionDetails>
          </Accordion>
        </Paper>
      </Grid>
      {checkPermissions([GAME_USER_DEPOSIT_VIEW], permissions) && (
        <Grid item xs={12}>
          <Paper elevation={0}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} px={4}>
                <Typography>User Deposits</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <UserDeposits />
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Grid>
      )}
      {checkPermissions([GAME_USER_WITHDRAWAL_VIEW], permissions) && (
        <Grid item xs={12}>
          <Paper elevation={0}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} px={4}>
                <Typography>User Withdraws</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <UserWithdraws />
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Grid>
      )}
      {checkPermissions([GAME_USER_REFUNDS_VIEW], permissions) && (
        <Grid item xs={12}>
          <Paper elevation={0}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} px={4}>
                <Typography>User Refunds</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <UserRefunds />
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Grid>
      )}
      {checkPermissions([GAME_USER_HISTORY_MONEY_VIEW], permissions) && (
        <Grid item xs={12}>
          <Paper elevation={0}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} px={4}>
                <Typography>User Account History</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <UserMoneyHistory />
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};

const UserInfo = ({ loading, title, value, link }) =>
  loading ? (
    <>
      <Skeleton variant="text" width={60} sx={{ fontSize: "0.7rem" }} />
      <Skeleton variant="text" width={120} sx={{ fontSize: "1rem" }} />
    </>
  ) : (
    <>
      <Typography variant="caption">{title}</Typography>
      {link ? (
        <Link component={NavLink} to={link}>
          <Typography variant="body2" component="div" noWrap>
            {value}
          </Typography>
        </Link>
      ) : (
        <Typography variant="body2" component="div" noWrap>
          {value}
        </Typography>
      )}
    </>
  );

// TODO: Create proper loading state UI for this
// TODO: Fix the problem with tables making requests all the time when edit on/off
function UserView() {
  const { userId } = useParams();
  const { permissions } = useAuth();
  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [options, setOptions] = useState({});
  const [user, setUser] = useState({
    id: null,
    firebaseId: null,
    name: null,
    type: null,
    email: null,
    status: null,
    cash: null,
    bonusCash: null,
    cashSum: null,
    chips: null,
    gamesAmount: {
      wins: null,
      forfeits: null,
      draws: null,
      winningStreaks: null,
    },
    country: null,
    createdAt: null,
    lastActiveAt: null,
  });

  const getUser = useCallback(async () => {
    try {
      setLoading(true);
      const { data: options } = await axios.options(`/api/v1/users/${userId}`);
      setOptions(options);

      const { data } = await axios.get(`/api/v1/users/${userId}`);
      setUser(data);
    } catch (e) {
      setError(e.message);
      console.log(e);
    }
    setLoading(false);
  }, [setLoading, setError, setUser, userId]);

  const [ConfirmDialog, confirmAction] = useConfirm(
    "Are you sure you want to update user?"
  );
  const updateUser = useCallback(
    async (
      {
        chips,
        bonusCash,
        reasonReward,
        reasonRewardText,
        status,
        reasonStatus,
        reasonStatusText,
      },
      { setErrors, setStatus, setSubmitting, resetForm }
    ) => {
      const isConfirmed = await confirmAction();

      if (!isConfirmed) {
        return;
      }

      try {
        setLoading(true);
        const { data } = await axios.post(`/api/v1/users/${userId}`, {
          ...(chips !== user.chips || bonusCash !== user.bonusCash
            ? {
                chips,
                bonusCash,
                reasonReward,
                reasonRewardText,
              }
            : {}),
          ...(status !== user.status
            ? {
                status,
                reasonStatus,
                reasonStatusText,
              }
            : {}),
        });

        resetForm();

        setUser(data);

        setEdit(false);
      } catch (error) {
        console.log(error);
        const message = error.messages || "Something went wrong";

        setStatus({ success: false });
        setErrors({ submit: message });
        setSubmitting(false);
      }
      setLoading(false);
    },
    [setLoading, setError, setUser, userId, user]
  );

  useEffect(() => {
    getUser();
  }, [getUser]);

  if (error) {
    return (
      <Alert mb={4} severity="error">
        {error}
      </Alert>
    );
  }

  return (
    <React.Fragment>
      <Helmet title="User View" />

      <Typography variant="h3" gutterBottom display="inline">
        User {userId}
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          <HomeIcon />
        </Link>
        <Link component={NavLink} to="/users">
          Users
        </Link>
        <Typography>{userId}</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <Grid container spacing={5}>
        <Grid item xs={12}>
          <Formik
            initialValues={{
              chips: user.chips,
              bonusCash: user.bonusCash,
              reasonReward: "",
              reasonRewardText: "",
              status: user.status,
              reasonStatus: "",
              reasonStatusText: "",
            }}
            enableReinitialize
            validationSchema={Yup.object().shape({
              chips: Yup.number().required("Chips Amount is required"),
              bonusCash: Yup.number().required("Bonus Cash Amount is required"),
              reasonReward: Yup.string().when(["chips", "bonusCash"], {
                is: (chips, bonusCash) =>
                  chips !== user.chips || bonusCash !== user.bonusCash,
                then: Yup.string().required("Reason is required"),
                otherwise: Yup.string(),
              }),
              reasonRewardText: Yup.string().when(["chips", "bonusCash"], {
                is: (chips, bonusCash) =>
                  chips !== user.chips || bonusCash !== user.bonusCash,
                then: Yup.string().required("Reason text is required"),
                otherwise: Yup.string(),
              }),
              status: Yup.number().required("Status is required"),
              reasonStatus: Yup.string().when("status", {
                is: (status) => status !== user.status,
                then: Yup.string().required("Reason is required"),
                otherwise: Yup.string(),
              }),
              reasonStatusText: Yup.string().when("status", {
                is: (status) => status !== user.status,
                then: Yup.string().required("Reason text is required"),
                otherwise: Yup.string(),
              }),
            })}
            onSubmit={updateUser}
          >
            {({ handleSubmit, isSubmitting, values, resetForm }) => (
              <form noValidate onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Grid container spacing={8}>
                          <Grid item xs={12}>
                            <Grid
                              container
                              spacing={2}
                              justifyContent="space-between"
                              alignItems={{ xs: "end", sm: "unset" }}
                              direction={{ xs: "column-reverse", sm: "row" }}
                              flexWrap="nowrap"
                            >
                              <Grid item>
                                <Grid
                                  container
                                  spacing={{ xs: 8, md: 12, lg: 20 }}
                                  direction="row"
                                >
                                  <Grid item xs="auto">
                                    <Grid
                                      container
                                      spacing={4}
                                      direction="column"
                                    >
                                      <Grid item xs={12}>
                                        <UserInfo
                                          title="Firebase User ID"
                                          value={user.firebaseId}
                                          loading={loading}
                                        />
                                      </Grid>
                                      <Grid item xs={12}>
                                        <UserInfo
                                          title="Inner User ID"
                                          value={user.id}
                                          loading={loading}
                                        />
                                      </Grid>
                                      <Grid item xs={12}>
                                        <UserInfo
                                          title="User Name"
                                          value={user.name}
                                          loading={loading}
                                        />
                                      </Grid>
                                      <Grid item xs={12}>
                                        <UserInfo
                                          title="User Type"
                                          value={USER_TYPES[user.type] || "-"}
                                          loading={loading}
                                        />
                                      </Grid>
                                      <Grid item xs={12}>
                                        <UserInfo
                                          title="E-mail"
                                          value={user.email}
                                          loading={loading}
                                        />
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                  <Grid item xs="auto">
                                    <Grid
                                      container
                                      spacing={4}
                                      direction="column"
                                    >
                                      <Grid item xs={12}>
                                        {edit ? (
                                          <Field
                                            name="chips"
                                            label="User Chips Amount"
                                            component={NumberField}
                                          />
                                        ) : (
                                          <UserInfo
                                            title="User Chips Amount"
                                            value={user.chips}
                                            loading={loading}
                                          />
                                        )}
                                      </Grid>
                                      <Grid item xs={12}>
                                        {edit ? (
                                          <Field
                                            name="bonusCash"
                                            label="User Bonus Cash Amount"
                                            component={NumberField}
                                          />
                                        ) : (
                                          <UserInfo
                                            title="User Bonus Cash Amount"
                                            value={convertUSD(user.bonusCash)}
                                            loading={loading}
                                          />
                                        )}
                                      </Grid>
                                      {edit && (
                                        <Grid item xs={12}>
                                          <Field
                                            name="reasonReward"
                                            label="Reason for Reward"
                                            component={SingleSelect}
                                            options={options.reasonReward}
                                          />
                                        </Grid>
                                      )}
                                      {edit && (
                                        <Grid item xs={12}>
                                          <Field
                                            name="reasonRewardText"
                                            label="Specify the reason"
                                            component={MultilineTextField}
                                          />
                                        </Grid>
                                      )}
                                      <Grid item xs={12}>
                                        <UserInfo
                                          title="User Real Cash Amount"
                                          value={convertUSD(user.cash)}
                                          loading={loading}
                                        />
                                      </Grid>
                                      <Grid item xs={12}>
                                        <UserInfo
                                          title="User Total Cash Amount"
                                          value={convertUSD(user.cashSum)}
                                          loading={loading}
                                        />
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                  <Grid item xs="auto">
                                    <Grid
                                      container
                                      spacing={4}
                                      direction="column"
                                    >
                                      <Grid item xs={12}>
                                        {edit ? (
                                          <Field
                                            name="status"
                                            label="User Status"
                                            component={SingleSelect}
                                            options={options.status}
                                          />
                                        ) : (
                                          <UserInfo
                                            component="div"
                                            title="User Status"
                                            value={
                                              <StatusChip
                                                options={COLORS_USER_STATUSES}
                                                value={user.status}
                                                label={
                                                  options?.status?.find(
                                                    (entry) =>
                                                      entry.value ===
                                                      user.status
                                                  )?.display_name
                                                }
                                              />
                                            }
                                            loading={loading}
                                          />
                                        )}
                                      </Grid>
                                      {edit && (
                                        <Grid item xs={12}>
                                          <Field
                                            name="reasonStatus"
                                            label="Reason for Status Change"
                                            component={SingleSelect}
                                            options={options.reason}
                                          />
                                        </Grid>
                                      )}
                                      {edit && (
                                        <Grid item xs={12}>
                                          <Field
                                            name="reasonStatusText"
                                            label="Specify the reason"
                                            component={MultilineTextField}
                                          />
                                        </Grid>
                                      )}
                                      <Grid item xs={12}>
                                        <UserInfo
                                          title="Create Date"
                                          value={convertUTC(user.createdAt)}
                                          loading={loading}
                                        />
                                      </Grid>
                                      <Grid item xs={12}>
                                        <UserInfo
                                          title="Last Active"
                                          value={convertUTC(user.lastActiveAt)}
                                          loading={loading}
                                        />
                                      </Grid>
                                      <Grid item xs={12}>
                                        <UserInfo
                                          title="Country"
                                          value={COUNTRIES[user.country] || "-"}
                                          loading={loading}
                                        />
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                              {checkPermissions(
                                [GAME_USER_MODIFY],
                                permissions
                              ) &&
                                !edit && (
                                  <Grid item xs="auto">
                                    <IconButton
                                      aria-label="edit"
                                      onClick={() => setEdit(true)}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </Grid>
                                )}
                            </Grid>
                          </Grid>
                          {edit && (
                            <Grid item xs={12}>
                              <Grid container spacing={2}>
                                <Grid item>
                                  <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={isSubmitting}
                                  >
                                    Confirm
                                  </Button>
                                </Grid>
                                <Grid item>
                                  <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting}
                                    onClick={() => {
                                      resetForm();
                                      setEdit(false);
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </Grid>
                              </Grid>
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Grid
                          container
                          spacing={{ xs: 8, md: 12, lg: 20 }}
                          direction="row"
                        >
                          <Grid item>
                            <UserInfo
                              title="Wins"
                              value={user.gamesAmount?.wins}
                              loading={loading}
                            />
                          </Grid>
                          <Grid item>
                            <UserInfo
                              title="Forfeits"
                              value={user.gamesAmount?.forfeits}
                              loading={loading}
                            />
                          </Grid>
                          <Grid item>
                            <UserInfo
                              title="Draws"
                              value={user.gamesAmount?.draws}
                              loading={loading}
                            />
                          </Grid>
                          <Grid item>
                            <UserInfo
                              title="Winning Streaks"
                              value={user.gamesAmount?.winningStreaks}
                              loading={loading}
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Grid>
        {!edit && (
          <Grid item xs={12}>
            <UserTables />
          </Grid>
        )}
      </Grid>
      <ConfirmDialog />
    </React.Fragment>
  );
}

export default UserView;
